import { IncomingMessage } from 'http';
import { CollectionHelpers } from 'src/helpers/CollectionHelpers';
import { mutate } from 'swr';

import { publicConfig } from '@public-config';

import { CreateProjectMetadata, FeatureLimit, Project, ProjectTeaser, SlackChannel } from 'models';
import { ProjectHelpers } from 'helpers/ProjectHelpers';
import { ServiceHelpers } from 'helpers/ServiceHelpers';

import { ProjectSearchFilters } from 'components/project-overview/hooks';
import { InternalTrackingService } from 'services/InternalTrackingService';
import { PermissionServiceSubscription } from 'services/PermissionServiceSubscription';
import { state as currentProjectState } from 'state/current-project';

import { ProjectMetadata } from '@/collections/projects/endpoints/metadata';
import type { RecentProject } from '@/collections/projects/endpoints/recent';
import { ProjectTeaserProps } from '@/collections/projects/endpoints/teaser';

import { ApiService } from './ApiService';
import { authHeaders, PayloadSDK } from './PayloadSDK';

const PAGE_SIZE = 24;

const ProjectService = {
    navigate: ServiceHelpers.createNavigator(ProjectHelpers.urls),

    generateName: ({ name, startFromExistingProject }: { name?: string; startFromExistingProject?: string }) => {
        return name ? (startFromExistingProject ? `Copy of ${name}` : name) : ProjectHelpers.defaults.name;
    },

    list: async ({ depth = 1, limit = 16, projectIds }: { depth: number; limit: number; projectIds?: string[] }) => {
        const params = projectIds?.map((id) => `where[id][in]=${id}`).join('&');

        return await ApiService.get(
            `${publicConfig.urls.api}/projects?depth=${depth}&limit=${limit}${params ? `&${params}` : ''}`,
        );
    },

    getSearchUrl: (page: number = 1, filters?: ProjectSearchFilters) => {
        const params = new URLSearchParams({
            depth: '0',
            sort: '-updatedAt',
            limit: String(PAGE_SIZE),
            page: String(page),
        });

        if (filters?.query) {
            params.append('where[name][like]', filters.query);
        }
        if (filters?.team) {
            params.append('where[team][equals]', filters.team);
        }
        if (filters?.customer) {
            params.append('where[customer][equals]', filters.customer);
        }
        if (filters?.tag) {
            params.append('where[tags][in]', filters.tag);
        }
        if (filters?.referenceDesigns) {
            params.append('where[isReferenceDesign][equals]', 'true');
        }
        if (filters?.companies?.length) {
            params.append('where[collaborators.companies.company][in]', filters.companies.join(','));
        }
        if (filters?.following !== undefined) {
            params.append('following', String(filters.following));

            return `${publicConfig.urls.api}/projects/search?${params.toString()}`;
        }

        return `${publicConfig.urls.api}/projects?${params.toString()}`;
    },

    getSupportProjectsMeta: async (companyIds: string[]): Promise<Pick<Project, 'id' | 'team' | 'createdBy'>[]> => {
        const { docs } = await PayloadSDK.find({
            collection: 'projects',
            where: {
                'collaborators.companies.company': {
                    in: companyIds,
                },
            },
            pagination: false,
            depth: 0,
            select: {
                team: true,
                createdBy: true,
            },
        });

        return docs as unknown as Pick<Project, 'id' | 'team' | 'createdBy'>[];
    },

    create: async (
        payload: CreateProjectMetadata,
    ): Promise<
        | {
              doc: Project;
              errors: undefined;
          }
        | {
              doc: undefined;
              errors: {
                  message: string;
              }[];
          }
    > => {
        InternalTrackingService.track('project.create', payload);

        const result = await ApiService.post(`${publicConfig.urls.api}/projects`, payload);

        await PermissionServiceSubscription.mutateFeatureLimit(FeatureLimit.PROJECTS);

        return result;
    },

    duplicate: async (
        projectId: Project['id'],
        name: Project['name'],
        isReferenceDesign: Project['isReferenceDesign'] | undefined,
    ) => {
        InternalTrackingService.track(
            'project.create',
            {
                fromProject: projectId,
                fromProjectIsReferenceDesign: isReferenceDesign,
                name,
            },
            { notifySlack: Boolean(isReferenceDesign), channel: SlackChannel.LOGGING },
        );

        const result = await ApiService.post(`${publicConfig.urls.api}/projects/${projectId}/duplicate?name=${name}`);

        await PermissionServiceSubscription.mutateFeatureLimit(FeatureLimit.PROJECTS);

        return result;
    },

    get: async (projectId: string, options: { depth?: number; req?: IncomingMessage } = {}) => {
        const { depth = 1, req } = options;

        const {
            docs: [project],
        } = await PayloadSDK.find(
            {
                collection: 'projects',
                where: {
                    or: [
                        ...(CollectionHelpers.isObjectIdString(projectId) ? [{ id: { equals: projectId } }] : []),
                        { slug: { equals: projectId } },
                    ],
                },
                limit: 1,
                depth,
            },
            { headers: authHeaders(req) },
        );

        return project || null;
    },

    /**
     * Access-free lookup of the project name, used for <head> metadata so shared links
     * preview with the project name even when the viewer (or a link crawler) has no access.
     */
    getMetadata: async (slugOrId: string, options: { req?: IncomingMessage } = {}) => {
        const project = await ApiService.get<ProjectMetadata | undefined>(
            `${publicConfig.urls.api}/projects/${slugOrId}/metadata`,
            { req: options.req, hideConsoleError: true },
        );

        return project ?? null;
    },

    getByDesign: async (designId: string) => {
        return ApiService.get(`${publicConfig.urls.api}/projects/get-by-design?designId=${designId}`);
    },

    update: async (projectId: string, payload: any) => {
        if (currentProjectState.project && currentProjectState.project.id === projectId) {
            InternalTrackingService.track('project.update', payload);

            Object.keys(payload).forEach((key) => {
                // @ts-ignore
                currentProjectState.project[key as any] = payload[key];
            });
        }

        const result = await ApiService.patch(`${publicConfig.urls.api}/projects/${projectId}`, payload, {
            throwError: true,
        });

        await mutate(`/api/projects/${projectId}?depth=0`, result.doc, { revalidate: false });

        return result;
    },

    delete: async (projectId: string) => {
        InternalTrackingService.track('project.delete');

        const result = await ApiService.post(
            `${publicConfig.urls.api}/projects/${projectId}/delete`,
            {},
            { throwError: true },
        );

        await mutate(
            ProjectHelpers.swr.recentProjects(),
            (projects: ProjectTeaser[] = []) => CollectionHelpers.exclude(projects, projectId, 'id'),
            { revalidate: false },
        );
        await PermissionServiceSubscription.mutateFeatureLimit(FeatureLimit.PROJECTS);

        return result;
    },

    deleteFile: async (project: Project['id'], fileId: string) => {
        return ApiService.post(`${publicConfig.urls.api}/projects/${project}/files/delete`, {
            fileId,
        });
    },

    getRecentProjects: async (): Promise<RecentProject[]> => {
        return ApiService.get(`${publicConfig.urls.api}/projects/recent`);
    },

    getTeamProjectCounts: async (req?: IncomingMessage): Promise<Record<string, number>> => {
        return await ApiService.get(`${publicConfig.urls.api}/projects/team-project-counts`, { req });
    },

    getTeaser: async (projectId: string) => {
        return ApiService.get<ProjectTeaserProps>(`${publicConfig.urls.api}/projects/${projectId}/teaser`);
    },
};

export { ProjectService };
